import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const SearchPopup = ({ context = "header" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  const open = () => setIsOpen(true);

  const close = () => {
    setIsOpen(false);
    setKeyword(""); // clear on close so next open starts fresh
  };

  // Icon color depends on context and page
  const iconColor =
    context === "mobile"
      ? "text-stone-700 hover:text-[#BC3F4A]"
      : isHome && !isOpen
      ? "text-[#f5f3ea]"
      : "text-stone-700";

  // Scroll lock + autofocus
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    return () => (document.body.style.overflow = "");
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  const submitHandler = (e) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?keyword=${encodeURIComponent(q)}` : "/search");
    close();
  };

  return (
    <>
      {/* Trigger */}
      <button
        onClick={open}
        aria-label="Open search"
        aria-expanded={isOpen}
        className="transition hover:text-[#BC3F4A]"
      >
        <Search
          className={`${iconColor} w-6 h-6 ml-1 transition-colors duration-300`}
          aria-hidden="true"
        />
      </button>

      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[1000] flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40"
              onClick={close}
              aria-hidden="true"
            />

            {/* Panel */}
            <motion.div
              initial={{ y: -80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -80, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full bg-[#f5f3ea] shadow-lg border-b border-[#e2e1d8]"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Search"
            >
              <form
                onSubmit={submitHandler}
                className="max-w-4xl w-full mx-auto flex items-center gap-3 px-4 sm:px-6 py-4"
              >
                <Search
                  className="w-5 h-5 text-stone-400 shrink-0"
                  aria-hidden="true"
                />
                <input
                  ref={inputRef}
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="Search products and articles…"
                  autoComplete="off"
                  className="flex-1 bg-white/90 focus:bg-white rounded-xl px-4 py-3 outline-none text-[#1c1917] placeholder-stone-400 text-base md:text-lg font-sourcesans shadow-inner"
                />
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 px-4 py-3 bg-[#BC3F4A] text-white rounded-xl hover:bg-[#a3323b] transition font-medium font-sourcesans disabled:opacity-50"
                  disabled={!keyword.trim()}
                >
                  <Search className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden sm:inline text-sm">Search</span>
                </button>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close search"
                  className="p-3 rounded-xl hover:bg-stone-200/60 transition text-stone-600"
                >
                  <X className="w-5 h-5" aria-hidden="true" />
                </button>
              </form>

             
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SearchPopup;